import React, { useEffect, useState } from 'react';
import { siteSettingsAPI, uploadAPI } from '../../services/api';
import '../admin/AdminComponents.css';

const SETTINGS_KEYS = 'hero_background_url,studios_hero_bg_url,shop_header_title,shop_header_subtitle,shop_header_bg_url';

const defaultValues = {
  hero_background_url: '',
  studios_hero_bg_url: '',
  shop_header_title: 'VAGUE SHOP',
  shop_header_subtitle: '',
  shop_header_bg_url: '',
};

const SiteSettingsManagement = () => {
  const [settings, setSettings] = useState(defaultValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingHeroBg, setUploadingHeroBg] = useState(false);
  const [uploadingStudiosBg, setUploadingStudiosBg] = useState(false);
  const [uploadingShopBg, setUploadingShopBg] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await siteSettingsAPI.get(SETTINGS_KEYS);
        if (response.success && response.data) {
          setSettings((prev) => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.error('Error loading site settings:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await siteSettingsAPI.update(settings);

      window.dispatchEvent(new CustomEvent('siteSettingsUpdated', {
        detail: response.success && response.data ? response.data : settings
      }));
    } catch (error) {
      console.error('Error saving settings:', error);
      alert(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (field, file, setUploading) => {
    if (!file) return;
    try {
      setUploading(true);
      const response = await uploadAPI.uploadImage(file);
      if (response.success && response.data?.imageUrl) {
        setSettings((prev) => ({ ...prev, [field]: response.data.imageUrl }));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-section">
        <div className="loading-message">Loading page settings...</div>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Page Settings</h2>
      </div>

      <form onSubmit={handleSave}>
        {/* ── Main Page Hero Background ── */}
        <div className="admin-settings-card">
          <h3 className="admin-settings-card-title">Main Page — Hero Background</h3>
          <p className="admin-settings-card-desc">
            Sets a custom background image on the hero / landing section. Leave blank to use the default gradient.
          </p>
          <div className="admin-form-group">
            <label className="admin-form-label">Background Image URL</label>
            <input
              className="admin-form-input"
              type="url"
              value={settings.hero_background_url || ''}
              onChange={(e) => setSettings((p) => ({ ...p, hero_background_url: e.target.value }))}
              placeholder="https://..."
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Or Upload Image</label>
            <input
              type="file"
              accept="image/*"
              disabled={uploadingHeroBg}
              onChange={(e) => handleUpload('hero_background_url', e.target.files?.[0], setUploadingHeroBg)}
            />
            {uploadingHeroBg && <small style={{ color: 'var(--text-secondary)' }}>Uploading...</small>}
          </div>
          {settings.hero_background_url && (
            <div className="admin-settings-preview">
              <img src={settings.hero_background_url} alt="Hero background preview" />
              <button
                type="button"
                className="admin-button secondary small"
                onClick={() => setSettings((p) => ({ ...p, hero_background_url: '' }))}
              >
                Remove
              </button>
            </div>
          )}
        </div>

        <div className="admin-settings-card">
          <h3 className="admin-settings-card-title">Studios - Hero Background</h3>
          <p className="admin-settings-card-desc">
            Sets a custom background image on the Veloura Studios page hero. Leave blank to use the default background.
          </p>
          <div className="admin-form-group">
            <label className="admin-form-label">Background Image URL</label>
            <input
              className="admin-form-input"
              type="url"
              value={settings.studios_hero_bg_url || ''}
              onChange={(e) => setSettings((p) => ({ ...p, studios_hero_bg_url: e.target.value }))}
              placeholder="https://..."
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Or Upload Image</label>
            <input
              type="file"
              accept="image/*"
              disabled={uploadingStudiosBg}
              onChange={(e) => handleUpload('studios_hero_bg_url', e.target.files?.[0], setUploadingStudiosBg)}
            />
            {uploadingStudiosBg && <small style={{ color: 'var(--text-secondary)' }}>Uploading...</small>}
          </div>
          {settings.studios_hero_bg_url && (
            <div className="admin-settings-preview">
              <img src={settings.studios_hero_bg_url} alt="Studios hero background preview" />
              <button
                type="button"
                className="admin-button secondary small"
                onClick={() => setSettings((p) => ({ ...p, studios_hero_bg_url: '' }))}
              >
                Remove
              </button>
            </div>
          )}
        </div>

        {/* ── Shop Header ── */}
        <div className="admin-settings-card">
          <h3 className="admin-settings-card-title">Shop — Header</h3>
          <p className="admin-settings-card-desc">
            Customise the title, subtitle, and background image shown at the top of the Shop page.
          </p>
          <div className="admin-form-group">
            <label className="admin-form-label">Header Title</label>
            <input
              className="admin-form-input"
              value={settings.shop_header_title || ''}
              onChange={(e) => setSettings((p) => ({ ...p, shop_header_title: e.target.value }))}
              placeholder="VAGUE SHOP"
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Header Subtitle</label>
            <input
              className="admin-form-input"
              value={settings.shop_header_subtitle || ''}
              onChange={(e) => setSettings((p) => ({ ...p, shop_header_subtitle: e.target.value }))}
              placeholder="Optional subtitle text"
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Header Background Image URL</label>
            <input
              className="admin-form-input"
              type="url"
              value={settings.shop_header_bg_url || ''}
              onChange={(e) => setSettings((p) => ({ ...p, shop_header_bg_url: e.target.value }))}
              placeholder="https://..."
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Or Upload Image</label>
            <input
              type="file"
              accept="image/*"
              disabled={uploadingShopBg}
              onChange={(e) => handleUpload('shop_header_bg_url', e.target.files?.[0], setUploadingShopBg)}
            />
            {uploadingShopBg && <small style={{ color: 'var(--text-secondary)' }}>Uploading...</small>}
          </div>
          {settings.shop_header_bg_url && (
            <div className="admin-settings-preview">
              <img src={settings.shop_header_bg_url} alt="Shop header background preview" />
              <button
                type="button"
                className="admin-button secondary small"
                onClick={() => setSettings((p) => ({ ...p, shop_header_bg_url: '' }))}
              >
                Remove
              </button>
            </div>
          )}
        </div>

        <div className="admin-form-actions" style={{ marginTop: '1.5rem' }}>
          <button type="submit" className="admin-button primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SiteSettingsManagement;
